import { Routes } from '@angular/router';
import { TenantUserRoles } from '@buspro/shared/common';
import { AuthRoutes } from '@buspro/web/tenants/auth';
import { TenantOnboardingRoutes } from '@buspro/web/tenants/onboarding';
import {
  FutureBookingReceiptPrintSheetComponent,
  ParcelReceiptPrintSheetComponent,
  ParcelStickerPrintSheetComponent,
  PassengerTicketPrintSheetComponent,
  PrintLayoutComponent,
} from '@buspro/web/tenants/printing';
import { AuthUserGuard } from '@corelabs/ng-auth';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { tenantAuthUserResolver } from './tenant-auth-user.resolver';

export const tenantRoutes: Routes = [
  AuthRoutes,
  ...TenantOnboardingRoutes,
  {
    path: 'print',
    outlet: 'print',
    canActivate: [AuthUserGuard],
    canActivateChild: [AuthUserGuard],
    component: PrintLayoutComponent,
    children: [
      {
        path: 'parcel-receipt/:parcelId',
        component: ParcelReceiptPrintSheetComponent,
      },
      {
        path: 'parcel-sticker/:parcelId',
        component: ParcelStickerPrintSheetComponent,
      },
      {
        path: 'passenger-ticket/:compositeKeys',
        component: PassengerTicketPrintSheetComponent,
      },
      {
        path: 'future-booking-receipt/:documentId',
        component: FutureBookingReceiptPrintSheetComponent,
      },
    ],
  },
  {
    path: '',
    canActivate: [AuthUserGuard],
    canActivateChild: [AuthUserGuard],
    loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.TenantWorkspaceLayoutComponent),
    resolve: {
      authUser: tenantAuthUserResolver,
    },
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () => import('@buspro/web/tenants/dashboard').then((m) => m.TenantDashboardComponent),
        data: {
          title: 'Dashboard',
        },
      },
      {
        path: 'notification-center',
        data: { title: 'Notification Center' },
        loadComponent: () =>
          import('@buspro/web/tenants/notification-center').then((m) => m.TenantNotificationCenterComponent),
      },
      {
        path: 'profile',
        loadComponent: () => import('@buspro/web/tenants/users').then((m) => m.UserProfileComponent),
        data: { title: 'My Profile' },
      },
      {
        path: 'monitor',
        loadChildren: () => import('@buspro/web/tenants/monitor').then((m) => m.MonitorModule),
      },
      {
        path: 'reports',
        loadChildren: () => import('@buspro/web/tenants/reports').then((m) => m.ReportsModule),
      },
      {
        path: 'operations',
        loadChildren: () => import('@buspro/web/tenants/operations').then((m) => m.OperationsModule),
      },
      {
        path: 'booking',
        loadChildren: () => import('@buspro/web/tenants/bookings').then((m) => m.BookingModule),
      },

      {
        path: 'parcels',
        loadChildren: () => import('@buspro/web/tenants/parcels').then((m) => m.ParcelsModule),
      },
      {
        path: 'terminals',
        loadChildren: () => import('@buspro/web/tenants/places').then((m) => m.TerminalsModule),
      },
      {
        path: 'bus-stops',
        loadChildren: () => import('@buspro/web/tenants/places').then((m) => m.BusStopsModule),
      },
      {
        path: 'routes',
        loadChildren: () => import('@buspro/web/tenants/routes').then((m) => m.RoutesModule),
      },
      {
        // Cash handovers (docs/CASH_ACCOUNTING_PLAN.md, Phase E).
        path: 'cash',
        loadChildren: () => import('@buspro/web/tenants/cash').then((m) => m.CashModule),
        canActivate: [ngxPermissionsGuard],
        data: {
          permissions: {
            only: [
              TenantUserRoles.TENANT_ADMIN,
              TenantUserRoles.TENANT_DIRECTOR,
              TenantUserRoles.TENANT_ACCOUNTANT,
              TenantUserRoles.TENANT_MANAGEMENT_MEMBER,
              TenantUserRoles.TENANT_TERMINAL_MANAGER,
              TenantUserRoles.TENANT_BOOKING_MANAGER,
              // Parcel Manager: counts the parcel desk in (the menu offered it,
              // but this guard turned them away). Clerks: My cash.
              TenantUserRoles.TENANT_PARCEL_MANAGER,
              TenantUserRoles.TERMINAL_BOOKING_CLERK,
              TenantUserRoles.TERMINAL_PARCEL_CLERK,
            ],
          },
        },
      },
      {
        path: 'refunds',
        loadChildren: () => import('@buspro/web/tenants/refunds').then((m) => m.RefundsModule),
      },

      {
        path: 'buses',
        loadChildren: () => import('@buspro/web/tenants/buses').then((m) => m.BusesModule),
      },
      {
        path: 'conductors',
        loadChildren: () => import('@buspro/web/tenants/conductors').then((m) => m.ConductorsModule),
      },
      {
        path: 'drivers',
        loadChildren: () => import('@buspro/web/tenants/drivers').then((m) => m.DriversModule),
      },
      {
        path: 'access-control',
        loadChildren: () => import('@buspro/web/tenants/users').then((m) => m.TenantUsersModule),
      },
      {
        path: 'billing',
        loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.TenantBillingPageComponent),
        canActivate: [ngxPermissionsGuard],
        data: {
          title: 'Billing',
          permissions: { only: [TenantUserRoles.TENANT_ADMIN] },
        },
      },
      {
        path: 'settings',
        loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.SettingsPageComponent),
        data: { title: 'Settings' },
        children: [
          {
            path: '',
            redirectTo: 'company',
            pathMatch: 'full',
          },
          {
            path: 'company',
            loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.CompanySettingsSectionComponent),
            data: { title: 'Company Settings' },
          },
          {
            path: 'sms-templates',
            loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.SmsTemplatesSectionComponent),
            data: { title: 'SMS Templates' },
          },
          {
            path: 'discounts',
            loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.DiscountsSectionComponent),
            data: { title: 'Discount Rates' },
          },
          {
            path: 'parcel-item-types',
            loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.ParcelItemTypesSectionComponent),
            data: { title: 'Parcel Item Types' },
          },
          {
            path: 'cash',
            loadComponent: () => import('@buspro/web/tenants/shared').then((c) => c.CashSettingsSectionComponent),
            data: { title: 'Cash Handovers' },
          },
        ],
      },

      // { path: '**', component: PageNotFoundComponent },
    ],
  },
  // { path: '**', component: PageNotFoundComponent },
];
